import { useEffect, useState, useMemo } from "react";
import { api, API_BASE } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/Primitives";
import { Plus, Play, Trash2, Pencil, FileStack, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useApiLoading, LoadingRow } from "../components/TableLoader";
import TemplateEditorModal from "../components/TemplateEditorModal";

const PLATFORM_LABEL = { meta: "Meta", dv360: "DV360", tiktok: "TikTok" };
const DATE_RANGE_LABEL = {
    today: "Today",
    yesterday: "Yesterday",
    last_7_days: "Last 7 days",
    last_15_days: "Last 15 days",
    last_30_days: "Last 30 days",
    last_60_days: "Last 60 days",
    last_90_days: "Last 90 days",
    mtd: "Month-to-date",
    last_month: "Last month",
    custom: "Custom",
};

export default function Templates() {
    const { user } = useAuth();
    const apiLoading = useApiLoading();
    const [list, setList] = useState([]);
    const [openEditor, setOpenEditor] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [runningId, setRunningId] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/report-templates");
            setList(data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load templates");
        }
    };
    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        if (!window.confirm("Delete this template? This cannot be undone.")) return;
        try {
            await api.delete(`/report-templates/${id}`);
            toast.success("Template deleted");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Delete failed");
        }
    };

    const run = async (t) => {
        setRunningId(t.id);
        try {
            const token = localStorage.getItem("wl_token");
            const resp = await fetch(`${API_BASE}/report-templates/${t.id}/run`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err.detail || `HTTP ${resp.status}`);
            }
            const payload = await resp.json();
            const fileList = Array.isArray(payload.files) ? payload.files : [];
            if (!fileList.length) {
                toast.error("No file was produced");
                return;
            }
            const b64ToBlob = (b64, mime) => {
                const bytes = atob(b64);
                const arr = new Uint8Array(bytes.length);
                for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
                return new Blob([arr], { type: mime || "application/octet-stream" });
            };
            for (let i = 0; i < fileList.length; i++) {
                const f = fileList[i];
                const blob = b64ToBlob(f.content_b64, f.media_type);
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = f.filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                // Small stagger so browsers reliably accept multiple downloads
                if (i < fileList.length - 1) await new Promise((r) => setTimeout(r, 400));
            }
            toast.success(
                fileList.length === 1
                    ? `Downloaded ${fileList[0].filename}`
                    : `Downloaded ${fileList.length} files`
            );
        } catch (e) {
            toast.error(e.message || "Run failed");
        } finally {
            setRunningId(null);
        }
    };

    const openNew = () => { setEditingId(null); setOpenEditor(true); };
    const openEdit = (id) => { setEditingId(id); setOpenEditor(true); };

    const totalCount = useMemo(() => list.length, [list]);

    return (
        <div data-testid="templates-page">
            <PageHeader
                eyebrow="One-click reports"
                title="Templates"
                subtitle="Save your favorite report definitions and download them on demand. Each template can output CSV, XLSX, or both."
                actions={
                    <button
                        onClick={openNew}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium"
                        style={{ background: "var(--wl-primary-hex)", color: "#ffffff" }}
                        data-testid="template-create-btn"
                    >
                        <Plus className="h-4 w-4" /> New template
                    </button>
                }
            />

            <div className="bg-white border border-zinc-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-zinc-100 border-b border-zinc-200">
                            <th className="overline text-left p-3">Name</th>
                            <th className="overline text-left p-3">Platform</th>
                            <th className="overline text-left p-3">Metrics</th>
                            <th className="overline text-left p-3">Date range</th>
                            <th className="overline text-left p-3">Formats</th>
                            <th className="overline text-left p-3">Owner</th>
                            <th className="overline text-right p-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((t) => (
                            <tr key={t.id} className="border-b border-zinc-200 last:border-0" data-testid={`template-row-${t.id}`}>
                                <td className="p-3">
                                    <div className="flex items-center gap-2">
                                        <FileStack className="h-4 w-4 text-zinc-400" />
                                        <span className="font-medium text-zinc-900" title={t.name}>{t.name}</span>
                                    </div>
                                </td>
                                <td className="p-3 text-zinc-700">{PLATFORM_LABEL[t.platform] || t.platform}</td>
                                <td className="p-3 text-zinc-500 text-xs">
                                    <span title={t.metrics.join(", ")}>{t.metrics.length} metric{t.metrics.length === 1 ? "" : "s"}</span>
                                    {t.breakdowns?.length ? (
                                        <span className="ml-2 px-1.5 py-0.5 rounded bg-zinc-100 text-[10px] uppercase" title={t.breakdowns.join(", ")}>
                                            {t.breakdowns.length === 1 ? t.breakdowns[0] : `${t.breakdowns.length} breakdowns`}
                                        </span>
                                    ) : null}
                                </td>
                                <td className="p-3 text-zinc-700 text-xs">
                                    {DATE_RANGE_LABEL[t.date_range_type] || t.date_range_type}
                                    {t.date_range_type === "custom" ? (
                                        <div className="text-zinc-400">{t.custom_start_date} → {t.custom_end_date}</div>
                                    ) : null}
                                </td>
                                <td className="p-3">
                                    <div className="flex gap-1">
                                        {t.formats.map((f) => (
                                            <span key={f} className="px-1.5 py-0.5 rounded text-[10px] uppercase bg-zinc-100 text-zinc-700 border border-zinc-200">{f}</span>
                                        ))}
                                    </div>
                                </td>
                                <td className="p-3 text-zinc-500 text-xs truncate max-w-[180px]" title={t.owner_email || ""}>{t.owner_email || "—"}</td>
                                <td className="p-3 text-right">
                                    <div className="inline-flex items-center gap-2 text-xs">
                                        <button
                                            onClick={() => run(t)}
                                            disabled={runningId === t.id}
                                            className="inline-flex items-center gap-1 text-zinc-600 hover:wl-accent-text disabled:opacity-50"
                                            data-testid={`template-run-${t.id}`}
                                        >
                                            {runningId === t.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                                            {runningId === t.id ? "running…" : "run"}
                                        </button>
                                        {t.can_edit && (
                                            <>
                                                <button onClick={() => openEdit(t.id)} className="inline-flex items-center gap-1 text-zinc-600 hover:wl-accent-text" data-testid={`template-edit-${t.id}`}>
                                                    <Pencil className="h-3.5 w-3.5" /> edit
                                                </button>
                                                <button onClick={() => remove(t.id)} className="inline-flex items-center gap-1 text-red-500 hover:text-red-400" data-testid={`template-delete-${t.id}`}>
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {totalCount === 0 && (apiLoading ? <LoadingRow colSpan={7} /> : (
                            <tr>
                                <td colSpan={7} className="p-10 text-center text-zinc-500" data-testid="templates-empty">
                                    No templates yet. Click <span className="font-medium">New template</span> to save your first one-click report.
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {openEditor && (
                <TemplateEditorModal
                    templateId={editingId}
                    onClose={() => setOpenEditor(false)}
                    onSaved={() => { setOpenEditor(false); load(); }}
                    currentUser={user}
                />
            )}
        </div>
    );
}
